import { useCallback, useEffect, useRef, useState } from "react";

interface AppleAuthorization {
  /** One-time authorization code - the only part the backend needs. */
  code: string;
  id_token: string;
  state?: string;
}

interface AppleSignInResponse {
  authorization: AppleAuthorization;
  /** Apple sends the user's name/e-mail only on the *first* authorization,
   * and never again. The backend persists it from that first call; the
   * frontend deliberately ignores it. */
  user?: {
    name?: { firstName?: string; lastName?: string };
    email?: string;
  };
}

interface AppleSDK {
  auth: {
    init: (config: {
      clientId: string;
      scope: string;
      redirectURI: string;
      usePopup: boolean;
    }) => void;
    signIn: () => Promise<AppleSignInResponse>;
  };
}

declare global {
  interface Window {
    AppleID?: AppleSDK;
  }
}

const APPLE_SCOPE = "name email";

/** Apple rejects the `signIn()` promise when the user simply dismisses the
 * popup. That is a cancellation, not a failure, so callers must not show an
 * error for it. */
const APPLE_CANCELLED_ERRORS = ["popup_closed_by_user", "user_cancelled_authorize"];

const isCancellation = (error: unknown): boolean => {
  const code =
    typeof error === "object" && error !== null
      ? (error as { error?: unknown }).error
      : undefined;
  return typeof code === "string" && APPLE_CANCELLED_ERRORS.includes(code);
};

/**
 * Wraps Sign in with Apple JS so Login/Signup only need to call
 * `triggerAppleSignIn()` and handle the resulting authorization code via
 * `onCode` - the same shape as `useGoogleSignIn`/`useFacebookSignIn`, except
 * Apple yields a one-time *code* rather than an access token: only the
 * backend can exchange it, since that requires Apple's client secret. The
 * SDK script itself is loaded in index.html; this hook waits for it, calls
 * `AppleID.auth.init()` once, and drives `AppleID.auth.signIn()`.
 *
 * Requires VITE_APPLE_CLIENT_ID and VITE_APPLE_REDIRECT_URI to be set - if
 * either is missing, `isReady` stays false so callers can hide/disable the
 * Apple button instead of throwing. With `usePopup: true` the redirect URI is
 * never actually navigated to (the result comes back via postMessage), but
 * Apple still validates it against the Return URLs registered for the
 * Service ID, so it must match one of them exactly.
 */
export const useAppleSignIn = (
  onCode: (authorizationCode: string) => void,
  onError?: (error: unknown) => void
) => {
  // Trimmed so a blank value (e.g. " " in the Amplify console) counts as unset.
  const clientId =
    (import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined)?.trim();
  const redirectURI =
    (import.meta.env.VITE_APPLE_REDIRECT_URI as string | undefined)?.trim();
  const [isReady, setIsReady] = useState(false);
  const initializedRef = useRef(false);

  // Keep the latest callbacks without re-running the setup effect below.
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!clientId || !redirectURI) {
      console.warn(
        "VITE_APPLE_CLIENT_ID / VITE_APPLE_REDIRECT_URI are not set - Apple sign-in is disabled."
      );
      return;
    }

    let cancelled = false;
    let pollId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const setup = () => {
      if (cancelled || !window.AppleID) return;
      // init() is meant to be called once per page load; guard against
      // re-running it if this effect re-fires (e.g. React StrictMode).
      if (!initializedRef.current) {
        window.AppleID.auth.init({
          clientId,
          scope: APPLE_SCOPE,
          redirectURI,
          usePopup: true,
        });
        initializedRef.current = true;
      }
      setIsReady(true);
    };

    if (window.AppleID) {
      setup();
    } else {
      // The Apple SDK script tag is async; poll briefly until it's loaded.
      pollId = setInterval(() => {
        if (window.AppleID) {
          clearInterval(pollId);
          setup();
        }
      }, 100);
      timeoutId = setTimeout(() => clearInterval(pollId), 10000);
    }

    return () => {
      cancelled = true;
      clearInterval(pollId);
      clearTimeout(timeoutId);
    };
  }, [clientId, redirectURI]);

  const triggerAppleSignIn = useCallback(async () => {
    if (!window.AppleID) return;
    try {
      const response = await window.AppleID.auth.signIn();
      onCodeRef.current(response.authorization.code);
    } catch (error) {
      // Dismissing the popup rejects too - report only real failures, so the
      // caller can drop its spinner either way.
      if (!isCancellation(error)) onErrorRef.current?.(error);
    }
  }, []);

  return { triggerAppleSignIn, isReady };
};
